import * as React from 'react'
import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { employeeUser } from 'modules/core/store/collections/users'

import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import employees from 'modules/employees/store/collections/employees'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import leaves from 'modules/timeoff/store/collections/leaves'

import LeaveOverlaps, { MAX_EMPLOYEE_USER_NAMES, OverlapsInfo } from './LeaveOverlaps'

interface Props {
  form: LeaveForm
}

const LeaveInfo = (props: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const { form } = props
  const [overlapsEmployees, setOverlapsEmployees] = useState<{
    inTeam: OverlapsInfo
    company: OverlapsInfo
  }>({
    inTeam: {
      userNames: [],
      total: 0,
    },
    company: {
      userNames: [],
      total: 0,
    },
  })
  const employeeId = Number(form.get('employee_id').value)

  const employee = employees.find({
    id: employeeId,
  })

  const startOn = form.get('start_on').value
  const finishOn = form.get('finish_on').value

  const canSeeCalendar = !currentEmployee || hasRegularAccess(currentEmployee.hasRegularAccess)

  React.useEffect(() => {
    if (!startOn || !finishOn || !canSeeCalendar) return

    if (!employee) return

    const formattedStartOn = dateUIToBackend(startOn)
    const formattedFinishOn = dateUIToBackend(finishOn)

    const fetchData = async () => {
      const data = await leaves.rpc('overlaps', {
        employee_id: employeeId,
        from: formattedStartOn,
        to: formattedFinishOn,
      })

      const mapEmployeeIdsToUserNameWithMax = (
        ids: number[],
        max = MAX_EMPLOYEE_USER_NAMES
      ): string[] => {
        const userNames = []
        for (const id of _.uniq(ids)) {
          if (userNames.length >= max) {
            break
          }

          const employee = employees.get(id)
          if (!employee) {
            throw Error(`Error getting employees with id = ${id}`)
          }

          userNames.push(employeeUser(employee).fullName())
        }
        return userNames
      }

      setOverlapsEmployees({
        company: {
          userNames: mapEmployeeIdsToUserNameWithMax(data.overlaps),
          total: data.overlaps.length,
        },
        inTeam: {
          userNames: mapEmployeeIdsToUserNameWithMax(data.in_team),
          total: data.in_team.length,
        },
      })
    }

    fetchData()
  }, [employeeId, finishOn, startOn])

  if (!employee) return null

  if (!overlapsEmployees.company.total) return null

  return (
    <Box
      paddingX="s24"
      paddingY="s16"
      background="grey400"
      borderRadius={{
        all: 'abs008',
      }}
    >
      <LeaveOverlaps
        accessId={employee.attributes.access_id}
        company={overlapsEmployees.company}
        inTeam={overlapsEmployees.inTeam}
      />
    </Box>
  )
}

export default observer(LeaveInfo)
